import { Link } from 'react-router-dom'
import { Mail, Calendar, User, Eye } from '@/ui/icons'
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/components'
import { Button } from '@/ui/design-system'
import { usePendingTripInvites } from '../hooks/usePendingTripInvites'
import { WaveLoader } from '@/ui/components'
import type { PendingTripInviteItem } from '@/shared/api/trips'
import { cn } from '@/shared/utils'

function formatDate(dateString: string | null): string {
  if (!dateString) return 'TBD'
  const [year, month, day] = dateString.split('-').map(Number)
  const date = new Date(year, month - 1, day)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })
}

export function PendingTripInvitesSection() {
  const { invites, isLoading, error } = usePendingTripInvites()

  if (isLoading) {
    return (
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <WaveLoader />
          </div>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return null
  }

  if (invites.length === 0) {
    return null
  }

  return (
    <Card className="mb-6 border-adventure-200 dark:border-adventure-800 bg-adventure-50/30 dark:bg-adventure-950/20">
      <CardHeader>
        <CardTitle className="text-lg font-semibold text-earth-900 dark:text-foreground flex items-center gap-2">
          <Mail className="h-5 w-5 text-adventure-600 dark:text-adventure-400" aria-hidden />
          Trip invitations
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-earth-600 dark:text-earth-400 mb-4">
          You’ve been invited to join these trips. Preview a trip to see details, then accept to join.
        </p>
        <ul className="space-y-3">
          {invites.map((invite) => (
            <PendingInviteRow key={invite.id} invite={invite} />
          ))}
        </ul>
      </CardContent>
    </Card>
  )
}

function PendingInviteRow({ invite }: { invite: PendingTripInviteItem }) {
  return (
    <li
      className={cn(
        'flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 rounded-lg',
        'bg-card border border-earth-200 dark:border-border'
      )}
    >
      <div className="min-w-0 flex-1">
        <p className="font-medium text-earth-900 dark:text-foreground truncate">{invite.trip_name}</p>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-xs text-earth-600 dark:text-earth-400">
          <span className="flex items-center gap-1">
            <User className="h-3.5 w-3.5 flex-shrink-0" aria-hidden />
            {invite.inviter_name}
          </span>
          <span className="flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5 flex-shrink-0" aria-hidden />
            {invite.start_date && invite.end_date
              ? `${formatDate(invite.start_date)} – ${formatDate(invite.end_date)}`
              : formatDate(invite.start_date)}
          </span>
        </div>
      </div>
      <Button variant="outline" size="sm" asChild className="w-full sm:w-auto shrink-0 gap-1.5">
        <Link to={`/trips/${invite.trip_id}`}>
          <Eye className="h-4 w-4" aria-hidden />
          Preview trip
        </Link>
      </Button>
    </li>
  )
}
